import { useEffect, useState } from 'react';
import { AlertCircle, Check, CheckCircle2, Crown, Loader2, RefreshCw, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { SubscriptionPlans } from './SubscriptionPlans';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';
import {
  PRICING_TIERS,
  calculateMaxActiveCompanies,
  calculateTierPrice,
  formatPrice,
  getOverageCount,
} from '../utils/pricingConfig';

/**
 * Firm › Billing › Overview, top card (RESTORE_PLAN R3.2, carrying the old
 * SubscriptionSettings overview): the plan with its trial badge, active users
 * with a sync button, included vs overage seats, the company limit, the
 * estimated monthly cost, the trial-end, cancel and over-limit warnings, the
 * plan's features, and Change Plan → the plan picker → Stripe Checkout.
 * Reads `GET /firm/:firmId` and `GET /billing/check-limits`.
 */

const API = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

export interface FirmBillingData {
  subscriptionTier: string;
  subscriptionStatus: string;
  trialEnd?: string | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
  userCount?: number;
  lastUserCountUpdate?: string | null;
  companyLimitMultiplier?: number;
}

export interface UserLimits {
  withinLimit: boolean;
  needsUpgrade: boolean;
  currentUsers: number;
  tierLimit?: number;
  recommendedTier?: string;
}

/** The numbers the card shows, from the firm's billing record. */
export function billingOverview(data: FirmBillingData | null) {
  const tierId = data?.subscriptionTier && PRICING_TIERS[data.subscriptionTier] ? data.subscriptionTier : 'free';
  const tier = PRICING_TIERS[tierId];
  const userCount = data?.userCount ?? 0;
  const multiplier = data?.companyLimitMultiplier ?? 5;
  return {
    tierId,
    tier,
    userCount,
    multiplier,
    included: tier.includedUsers,
    overage: getOverageCount(tierId, userCount),
    companyLimit: calculateMaxActiveCompanies(tierId, userCount, multiplier),
    companyLimitNote: tierId === 'free' ? 'active company' : `active companies (${userCount} × ${multiplier})`,
    estimatedCost: calculateTierPrice(tierId, userCount),
    isTrial: data?.subscriptionStatus === 'trialing' || data?.subscriptionStatus === 'trial',
  };
}

/** What Stripe Checkout sent the browser back with (`/billing?checkout=…`). */
export function checkoutOutcome(search: string): 'success' | 'canceled' | null {
  const value = new URLSearchParams(search).get('checkout');
  return value === 'success' || value === 'canceled' ? value : null;
}

const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : '');

/** Leaving for Stripe Checkout (one place, so a test can watch it). */
export const checkoutRedirect = {
  go(url: string) {
    window.location.href = url;
  },
};

interface SubscriptionOverviewCardProps {
  accessToken: string;
  firmId: string;
  /** Firm admins and billing admins change the plan; everyone else sees the numbers. */
  canManage: boolean;
}

export function SubscriptionOverviewCard({ accessToken, firmId, canManage }: SubscriptionOverviewCardProps) {
  const [data, setData] = useState<FirmBillingData | null>(null);
  const [limits, setLimits] = useState<UserLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<'success' | 'canceled' | null>(() =>
    typeof window === 'undefined' ? null : checkoutOutcome(window.location.search),
  );

  const loadFirm = async () => {
    const res = await fetch(`${API}/firm/${firmId}`, { headers: getAuthHeaders(accessToken) });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Could not load the subscription (${res.status})`);
    }
    setData(await res.json());
  };

  const loadLimits = async () => {
    const res = await fetch(`${API}/billing/check-limits`, { headers: getAuthHeaders(accessToken) });
    setLimits(res.ok ? await res.json() : null);
  };

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      await Promise.all([loadFirm(), canManage ? loadLimits() : Promise.resolve()]);
    } catch (e: any) {
      setLoadError(e?.message || 'Could not load the subscription');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [firmId, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncUsers = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/billing/sync-users`, { method: 'POST', headers: getAuthHeaders(accessToken) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not sync the user count');
      toast.success(`User count synced: ${body.userCount} ${body.userCount === 1 ? 'user' : 'users'}`);
      await Promise.all([loadFirm(), loadLimits()]);
    } catch (e: any) {
      toast.error(e?.message || 'Could not sync the user count');
    } finally {
      setSyncing(false);
    }
  };

  const choosePlan = async (tier: string) => {
    setCheckingOut(tier);
    try {
      const res = await fetch(`${API}/billing/create-checkout`, {
        method: 'POST',
        headers: getAuthHeadersWithJson(accessToken),
        body: JSON.stringify({ tier }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.sessionUrl) throw new Error(body.error || 'Could not start checkout');
      checkoutRedirect.go(body.sessionUrl);
    } catch (e: any) {
      toast.error(e?.message || 'Could not start checkout');
      setCheckingOut(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 flex justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" aria-label="Loading subscription" />
        </CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertDescription>{loadError}</AlertDescription>
      </Alert>
    );
  }

  const o = billingOverview(data);

  return (
    <div className="space-y-4">
      {outcome === 'success' && (
        <Alert>
          <CheckCircle2 className="size-4 text-green-600" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Checkout complete. Your plan updates here as soon as Stripe confirms the subscription.</span>
            <Button size="sm" variant="ghost" onClick={() => { setOutcome(null); void load(); }}>Refresh</Button>
          </AlertDescription>
        </Alert>
      )}
      {outcome === 'canceled' && (
        <Alert>
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Checkout canceled. Your plan has not changed.</span>
            <Button size="sm" variant="ghost" onClick={() => setOutcome(null)}>Dismiss</Button>
          </AlertDescription>
        </Alert>
      )}

      {limits?.needsUpgrade && (
        <Alert variant="destructive" role="alert">
          <AlertCircle className="size-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>
              You have {limits.currentUsers} users but your {o.tier.displayName} plan includes {limits.tierLimit}. Upgrade to keep adding people.
            </span>
            {canManage && <Button size="sm" variant="outline" onClick={() => setPlansOpen(true)}>Upgrade Now</Button>}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Current Subscription</CardTitle>
              <CardDescription>Your plan, seats and company limit</CardDescription>
            </div>
            {canManage && (
              <Button onClick={() => setPlansOpen(true)}>
                <Crown className="size-4 mr-2" aria-hidden="true" />
                Change Plan
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-muted-foreground mb-1">Current Plan</p>
              <div className="flex items-center gap-2">
                <Badge variant={o.tierId === 'free' ? 'outline' : 'default'}>{o.tier.displayName}</Badge>
                {o.isTrial && <Badge variant="secondary">Trial</Badge>}
              </div>
            </div>

            <div>
              <p className="text-muted-foreground mb-1">Active Users</p>
              <div className="flex items-center gap-2">
                <Users className="size-4 text-muted-foreground" aria-hidden="true" />
                <span data-testid="billing-user-count">{o.userCount}</span>
                {canManage && (
                  <Button variant="ghost" size="sm" onClick={syncUsers} disabled={syncing} aria-label="Sync user count" title="Sync user count">
                    <RefreshCw className={`size-3 ${syncing ? 'animate-spin' : ''}`} aria-hidden="true" />
                  </Button>
                )}
              </div>
              <p className="text-muted-foreground mt-1">
                {o.included} included{o.overage > 0 ? `, ${o.overage} overage` : ''}
              </p>
              {data?.lastUserCountUpdate && (
                <p className="text-muted-foreground">Synced {formatDate(data.lastUserCountUpdate)}</p>
              )}
            </div>

            <div>
              <p className="text-muted-foreground mb-1">Company Limit</p>
              <p data-testid="billing-company-limit">{o.companyLimit}</p>
              <p className="text-muted-foreground mt-1">{o.companyLimitNote}</p>
            </div>

            <div>
              <p className="text-muted-foreground mb-1">Estimated Cost</p>
              <p data-testid="billing-estimated-cost">{formatPrice(o.estimatedCost)}</p>
              <p className="text-muted-foreground mt-1">per month</p>
            </div>
          </div>

          {o.isTrial && data?.trialEnd && (
            <Alert>
              <AlertDescription>Your trial ends on {formatDate(data.trialEnd)}.</AlertDescription>
            </Alert>
          )}

          {data?.cancelAtPeriodEnd && data.currentPeriodEnd && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>Your subscription will be canceled on {formatDate(data.currentPeriodEnd)}.</AlertDescription>
            </Alert>
          )}

          <div>
            <h4 className="mb-3">Plan Features</h4>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {o.tier.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <Check className="size-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>

      <Dialog open={plansOpen} onOpenChange={(open) => { if (!checkingOut) setPlansOpen(open); }}>
        <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Choose Your Plan</DialogTitle>
            <DialogDescription>
              {checkingOut ? `Opening Stripe Checkout for ${PRICING_TIERS[checkingOut]?.displayName ?? checkingOut}…` : 'You finish the change on Stripe Checkout.'}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <SubscriptionPlans
              currentPlan={o.tierId}
              currentUserCount={o.userCount}
              onSelectPlan={checkingOut ? undefined : (planId) => void choosePlan(planId)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
